'use client'

import { Box, Flex, Input, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import styles from '@/app/[locale]/price/[slug]/_components/Trade/PriceConvertor/CoinListModal/index.module.css'
import CheckIcon from '@/assets/icons/checkmark.svg'
import Close from '@/assets/icons/cross-circle-fill.svg'
import Search from '@/assets/icons/search.svg'
import BaseIcon from '@/components/basic/BaseIcon'
import ResponsiveModal from '@/components/basic/ResponsiveModal'
import { allCurrencies } from '@/configs/currencies'
import type { Currency } from '@/types/global'
import { CurrencyType } from '@/types/global'

interface CurrencySelectModalProps {
  visible: boolean
  onClose: () => void
  onSelect: (currency: Currency) => void
  currentCurrencyCode: string
}

export default function CurrencySelectModal({
  visible,
  onClose,
  onSelect,
  currentCurrencyCode,
}: CurrencySelectModalProps) {
  const tCurrencySelectModal = useTranslations('currencySelectModal')

  const tCurrencySelector = useTranslations('currencySelector')
  const [keyword, setKeyword] = useState('')

  const filteredCurrencies = useMemo(() => {
    const lowerKeyword = keyword.toLowerCase()
    return allCurrencies.filter(
      c =>
        c.code.toLowerCase().includes(lowerKeyword) ||
        c.name.toLowerCase().includes(lowerKeyword) ||
        (c.nameKey && tCurrencySelector(c.nameKey).toLowerCase().includes(lowerKeyword)),
    )
  }, [keyword, tCurrencySelector])

  return (
    <ResponsiveModal
      title={<Text variant={'heading2'}>{tCurrencySelectModal('selectCurrency')}</Text>}
      opened={visible}
      onClose={onClose}
    >
      <Box>
        <Input
          classNames={{ input: styles.searchInput }}
          placeholder={tCurrencySelectModal('searchCurrency')}
          leftSection={
            <Box mx={12}>
              <Search width={20} height={20} fill={'var(--content-secondary)'} />
            </Box>
          }
          rightSectionPointerEvents="all"
          rightSection={
            keyword ? <Close className={styles.clearIcon} onClick={() => setKeyword('')} /> : null
          }
          value={keyword}
          onChange={event => setKeyword(event.target.value)}
        />

        <Stack mt={16}>
          <ScrollArea
            h={{ base: 'calc(100dvh - 160px)', md: 500 }}
            scrollbars={'y'}
            type={'scroll'}
          >
            {filteredCurrencies.map(currency => (
              <UnstyledButton
                key={currency.code}
                onClick={() => onSelect(currency)}
                style={{ width: '100%' }}
              >
                <Flex className={styles.coinRow}>
                  {currency.type === CurrencyType.Fiat ? (
                    <Image src={currency.iconUrl} alt={currency.code} width={28} height={28} />
                  ) : (
                    <BaseIcon src={currency.iconUrl} size={28} circle alt={currency.code} />
                  )}
                  <Stack gap={2} ml={8} flex={1}>
                    <Text variant={'body'}>{currency.code}</Text>
                    <Text variant={'subtitle'} c={'var(--content-secondary)'}>
                      {currency.nameKey ? tCurrencySelector(currency.nameKey) : currency.name}
                    </Text>
                  </Stack>
                  {currency.code === currentCurrencyCode && (
                    <CheckIcon width={24} height={24} fill="var(--content-primary)" />
                  )}
                </Flex>
              </UnstyledButton>
            ))}
            {filteredCurrencies.length === 0 && (
              <Flex h={200} align="center" justify="center">
                <Text c="var(--content-secondary)">{tCurrencySelectModal('noResultsFound')}</Text>
              </Flex>
            )}
          </ScrollArea>
        </Stack>
      </Box>
    </ResponsiveModal>
  )
}
